/**
 * What the HUD's menu and help buttons open (docs/ux.md §2 "⋯ menu", §14): settings kept on
 * the device, the game's own actions (resign, draw), and a short guide with the key list.
 */
import { otherSide, type Action, type GameState } from '@brig/rules-engine';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';
import type { MapStyle } from '../board/chartStyle.ts';
import type { HintMemory } from '../learn/hintMemory.ts';
import * as game from './Game.css.ts';
import { current } from './history.ts';
import * as css from './Hud.css.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';
import { KeysList } from './ViewControls.tsx';

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={css.section} aria-label={title}>
      <h3 className={css.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={css.setting}>
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
    </label>
  );
}

/** §14: per-device settings. */
export function SettingsMenu({
  snapshot,
  send,
  mapStyle,
  onMapStyle,
  hints,
}: {
  snapshot: TurnSnapshot;
  send: TurnSend;
  mapStyle: MapStyle;
  onMapStyle: (s: MapStyle) => void;
  /** First-time hints (U-LEARN-08): kept with the captain, not on the device. */
  hints: HintMemory;
}) {
  const { t } = useTranslation();
  return (
    <Section title={t('game.menu.settings')}>
      <div className={css.setting}>
        <LanguageSwitcher tone="paper" />
      </div>
      <Toggle
        label={t('game.menu.chart')}
        checked={snapshot.context.view === 'chart'}
        onChange={() => {
          send({ type: 'TOGGLE_VIEW' });
        }}
      />
      <Toggle
        label={t('game.menu.watercolour')}
        checked={mapStyle === 'watercolour'}
        onChange={(v) => {
          onMapStyle(v ? 'watercolour' : 'chart');
        }}
      />
      <Toggle
        label={t('game.menu.confirmCast')}
        checked={snapshot.context.confirmCast}
        onChange={(v) => {
          send({ type: 'SET_CONFIRM_CAST', value: v });
        }}
      />
      <Toggle label={t('learn.hintsOn')} checked={hints.on} onChange={hints.setOn} />
      <button type="button" className={game.button} disabled={hints.seen.size === 0} onClick={hints.reset}>
        {t('learn.hintsReset')}
      </button>
    </Section>
  );
}

/** R-TURN-08: resigning and the draw are open to the side to act at any time. */
export function GameMenu({ snapshot, onApply }: { snapshot: TurnSnapshot; onApply: (a: Action) => void }) {
  const { t } = useTranslation();
  const state: GameState = current(snapshot.context.history);
  if (state.status.kind !== 'playing') return null;
  // Against the computer or online, the player speaks for their own side, whoever is to move.
  const remote = snapshot.context.remote;
  const side = remote ? otherSide(remote) : state.turn.side;
  const name = (s: typeof side) => state.players[s].name;
  const offer = state.drawOffer;
  return (
    <Section title={t('game.menu.game')}>
      <div className={game.cardRow} style={{ justifyContent: 'flex-start' }}>
        <button
          type="button"
          className={game.button}
          onClick={() => {
            onApply({ type: 'offerDraw', side });
          }}
        >
          {t('game.menu.offerDraw')}
        </button>
        {offer && offer.by !== side ? (
          <button
            type="button"
            className={game.button}
            onClick={() => {
              onApply({ type: 'acceptDraw', side: otherSide(offer.by) });
            }}
          >
            {t('game.menu.acceptDraw', { side: name(otherSide(offer.by)) })}
          </button>
        ) : null}
        <button
          type="button"
          className={game.danger}
          onClick={() => {
            onApply({ type: 'resign', side });
          }}
        >
          {t('game.menu.resign', { side: name(side) })}
        </button>
      </div>
    </Section>
  );
}

const STEPS = ['muster', 'wind', 'battle', 'end'] as const;

/** A short guide to the turn and the key list. */
export function HelpMenu() {
  const { t } = useTranslation();
  return (
    <>
      <Section title={t('game.help.turn')}>
        <ol className={css.steps}>
          {STEPS.map((step) => (
            <li key={step}>
              <strong>{t(`game.help.steps.${step}.title`)}</strong> — {t(`game.help.steps.${step}.body`)}
            </li>
          ))}
        </ol>
      </Section>
      <Section title={t('game.help.select')}>
        <p className={game.hint} style={{ fontSize: 'inherit', margin: 0 }}>
          {t('game.help.selectBody')}
        </p>
      </Section>
      <Section title={t('game.keys.title')}>
        <KeysList />
      </Section>
    </>
  );
}
